<!--
  SettlementRateCard.vue
  콜봇 월 정산 · 읽기 전용 요율 표. 두 화면이 같이 쓴다.
  - mode 'effective' — 병원 상세 요율 탭(화면 명세 2-1)의 "유효 요율": 이 달 기본 버전에 이 회사 예외 칸을 덮은 값(상세 응답
    `rates`). 항목마다 출처 배지(기본 회색 / 예외 노랑), 예외 항목 아래 그 달 기본값(회색, `default_value`), 이 달 사용량이 쓴
    항목 강조('초과 161분 20초'), 쓰였는데 판매가가 없는 항목(미정 회선) 경고색. 구분(병원·내부·테스트) 행이 맨 위에 붙는다.
  - mode 'plan' — 기본 요율 화면(화면 명세 3)의 버전 하나(`GET .../default-plans` 의 items[]). 출처 배지 없음.
    `bare` 면 카드 테두리·머리 없이 표만 그린다(버전 머리는 DefaultPlanHistory 가 그린다).

  표(2026-10-27 목업 Rates.dc.html): 항목 | 판매가 | 원가 | 마진 | 계산 방법. 묶음은
  기본(월 사용료 · 포함 통화 · 월 사용료에 포함) → 통화 · 10초 단위(120분 초과 통화 · 발신 건당 · 받은 발신 통화) →
  회선 · 웹 텍스트(추가 070 회선 · 대표번호 회선 · 웹 텍스트) → 상담사 번호(추가 번호 — 블록이 없는 버전·달은 '요금 없음',
  ~/md/front/admin-콜봇-상담사번호-추가요금-프론트요청.md A3) → 상담사 연결 원가(판매가 없음) → 부가 항목(원가 + '미정'). 모든 묶음·행을
  늘 펼쳐 보인다(접힘 없음).
  판매가가 없으면 '미정' 배지, 판매가가 원가보다 낮으면 마진 칸에 '원가 미만'. 월 사용료·포함 통화·상담사 번호는 원가가 없다.
  부가세(월 사용료만 VAT 포함, 나머지 별도)는 판매가 머리의 툴팁으로 한 번만 말한다.
  요금표 v70 이전 모양은 표로 읽지 않고 안내와 요약만 보인다 — effective 는 부모가 넘기는 `legacy`(상세
  `statement.legacy`, 개정 8 — 스냅샷 요율이 예전 모양), plan 은 버전의 `legacy`(개정 1). 모양을 보고 짐작하지 않는다.

  데이터
  - rates: 상세 `rates`(덮을 칸마다 `{ value, source, default_value? }`) 또는 기본 요율 버전 — settlementRules.readCell 로 같은
    모양으로 읽는다. null 이면 빈 상태(그 달 기본 요율 없음 — DEFAULT_PLAN_MISSING).
  - costRates: `cost_rates`(읽기 전용 원가). 칸의 원가는 settlementRules.cellCostInfo 로 읽는다.
    effective 는 rates 에 실린 원가 스냅샷이 먼저다 — 마감 시점 표가 오늘 원가로 바뀌지 않는다. 스냅샷에 없는 원가(발신 기본료)만
    cost_rates 를 쓴다. plan 은 cost_rates 가 먼저다(화면 명세 3 — 판매가 옆 원가는 서버 cost_rates).
  - defaultRates(effective): 그 달 기본 요율 버전 — 예외 칸에 `default_value` 가 없을 때(예전 응답)만 기본값을 여기서 읽는다.
  - usageNotes(effective): settlementRules.cellUsageNotes(상세) — 이 달 쓴 칸과 그 양.
  - warnings(effective): 상세 `warnings` — 판매가 없이 쓰인 칸(missingPriceCellKeys — 미정 회선)을 경고색으로.
  - rates 가 없을 때(effective)의 까닭과 할 일은 요율 탭 위 안내가 말한다 — 표 자리에는 한 문장만 둔다.
  - snapshot: '마감 시점 요율' 배지(확정·검토 필요 달은 rates 가 정산 스냅샷 값이다).
  - 머리(bare 가 아닐 때): effective '이 달 요율'(화면 글자 — 코드·주석의 '유효 요율'과 같은 것) + 적용 버전 칩("기본 2026-09 · 예외 없음", 툴팁에 덮은 칸), plan '{월} 버전'.
  - 슬롯 header-extra: 머리 오른쪽(예: `기본 요율 화면 →` 링크, `[예외 추가/수정]` 버튼).
    yellow-button 을 넣으면 그 스타일은 부모 컴포넌트가 들고 있어야 한다(슬롯 내용은 부모 scope 다).
-->
<template>
  <section class="rate-card" :class="{ 'is-bare': bare }" :aria-labelledby="bare ? undefined : titleId" :aria-label="bare ? ariaLabel : undefined">
    <header v-if="!bare" class="rate-card__head">
      <h2 :id="titleId" class="rate-card__title">{{ cardTitle }}</h2>
      <a-tooltip v-if="isEffective && rates" :title="versionTooltip">
        <span class="rate-card__chip">{{ versionText }}</span>
      </a-tooltip>
      <span v-if="legacy" class="rate-card__chip is-legacy">v70 이전 모양</span>
      <a-tooltip v-if="snapshot" :title="SNAPSHOT_TOOLTIP">
        <span class="rate-card__chip is-snapshot">마감 시점 요율</span>
      </a-tooltip>
      <div v-if="$slots['header-extra']" class="rate-card__extra">
        <slot name="header-extra" />
      </div>
    </header>

    <p v-if="!rates" class="rate-card__empty">{{ emptyText }}</p>

    <!-- 요금표 v70 이전 모양 — 표로 읽지 않는다 -->
    <div v-else-if="legacy" class="rate-card__legacy" role="note">
      <p>{{ legacyText }}</p>
      <ul v-if="legacySummary.length" class="rate-card__legacy-list" aria-label="저장된 예전 칸">
        <li v-for="line in legacySummary" :key="line">{{ line }}</li>
      </ul>
    </div>

    <template v-else>
      <div class="rate-card__scroll">
        <table class="rate-table">
          <thead>
            <tr>
              <th scope="col" class="rate-table__item">항목</th>
              <th scope="col">
                <a-tooltip :title="PRICE_HEAD_TOOLTIP">
                  <span class="rate-table__help">판매가 <QuestionCircleOutlined aria-hidden="true" /></span>
                </a-tooltip>
              </th>
              <th scope="col">원가</th>
              <th scope="col" class="is-right">마진</th>
              <th scope="col">계산 방법</th>
            </tr>
          </thead>
          <tbody v-for="group in groups" :key="group.key">
            <tr class="rate-table__group">
              <th scope="colgroup" colspan="5">
                {{ group.title }}<span v-if="group.sub" class="rate-table__group-sub"> · {{ group.sub }}</span>
              </th>
            </tr>
            <tr v-for="row in group.rows" :key="row.key" :class="rowClass(row)">
              <th scope="row" class="rate-table__item">
                <span class="rate-table__label">{{ row.label }}</span>
                <span v-if="row.sourceLabel" class="rate-pill" :class="`is-${row.source}`">{{ row.sourceLabel }}</span>
                <span v-if="row.sub" class="rate-table__sub">{{ row.sub }}</span>
                <span v-if="row.usedText" class="rate-pill is-used" :class="{ 'is-warn': row.warn }" title="이 달 사용량">{{ row.usedText }}</span>
              </th>

              <!-- 월 사용료에 포함 — 한 칸에 이어 쓴다 -->
              <td v-if="row.span" colspan="4" class="rate-table__span">{{ row.spanText }}</td>

              <template v-else>
                <td class="rate-table__price">
                  <span v-if="row.unset" class="rate-pill is-unset">미정</span>
                  <template v-else>
                    <span class="rate-table__price-main">{{ row.priceMain }}</span>
                    <span v-if="row.priceSuffix" class="rate-table__price-suffix">{{ row.priceSuffix }}</span>
                  </template>
                  <span v-if="row.defaultText" class="rate-table__default">{{ row.defaultText }}</span>
                  <span v-if="row.warnText" class="rate-table__warn">{{ row.warnText }}</span>
                </td>
                <td class="rate-table__cost">
                  <span v-if="row.costUnknown" class="rate-pill is-pending">원가 확인 중</span>
                  <template v-else>{{ row.costText }}</template>
                  <small v-if="row.costNote" class="rate-table__cost-note">{{ row.costNote }}</small>
                </td>
                <td class="rate-table__margin is-right" :class="{ 'is-below': row.below }">
                  {{ row.marginText }}
                  <span v-if="row.below" class="rate-table__below">원가 미만</span>
                </td>
                <td class="rate-table__how" :title="row.howTitle || undefined">{{ row.how }}</td>
              </template>
            </tr>
            <tr v-if="group.note" class="rate-table__note-row">
              <td colspan="5" :title="group.noteTitle || undefined">{{ group.note }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </template>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

import { EMPTY, formatNumber, formatUnitPrice, isNumber } from '../service/settlementFormat';
import {
  COUNSELOR_LINE_NO_RATE_TEXT,
  LEGACY_RATES_TEXT,
  RATE_CELL_MAP,
  RATE_GROUP_LABELS,
  STT_BASIS_LABEL,
  categoryLabel,
  cellCostInfo,
  counselorCostRows,
  counselorNumberRates,
  extraItemRows,
  formatCostValue,
  formatRateValue,
  getPath,
  isCostUnknown,
  isLegacyPlan,
  legacyPlanSummary,
  missingPriceCellKeys,
  overriddenCellKeys,
  rateSourceLabel,
  readCell,
  tenSecondEquivalentText,
  versionsLabel,
} from '../service/settlementRules';

const props = defineProps({
  /** 상세 `rates`(유효 요율, 덮을 칸마다 source) 또는 기본 요율 버전 하나. null = 그 달 기본 요율 없음 */
  rates: { type: Object, default: null },
  /** `cost_rates`(읽기 전용 원가) — effective 는 rates 의 원가 스냅샷 다음, plan 은 먼저(머리말) */
  costRates: { type: Object, default: null },
  /** 'effective'(상세 요율 탭) | 'plan'(기본 요율 화면) */
  mode: {
    type: String,
    default: 'effective',
    validator: (v) => v === 'effective' || v === 'plan',
  },
  /** 카드 테두리·머리 없이 표만(기본 요율 화면 — 버전 머리는 부모가 그린다) */
  bare: { type: Boolean, default: false },
  /** effective: 그 달 기본 요율 버전 — 예외 칸에 default_value 가 없을 때의 기본값 */
  defaultRates: { type: Object, default: null },
  /** effective: 이 달 쓴 칸과 그 양(settlementRules.cellUsageNotes) — 칸 key → '초과 161분 20초' */
  usageNotes: { type: Object, default: () => ({}) },
  /** effective: 상세 `warnings` — 판매가 없이 쓰인 칸 경고색 */
  warnings: { type: Array, default: () => [] },
  /** effective: v70 이전에 만든 정산서(`statement.legacy`) — rates 가 예전 모양이라 표 대신 안내를 보인다 */
  legacy: { type: Boolean, default: false },
  /** '마감 시점 요율' 배지 */
  snapshot: { type: Boolean, default: false },
});

const SNAPSHOT_TOOLTIP =
  '이 달은 확정됐거나 검토 필요라 정산 스냅샷의 요율입니다. 마감 뒤 기본 요율·예외를 고쳐도 이 표는 바뀌지 않습니다.';
const PRICE_HEAD_TOOLTIP = '월 사용료만 VAT 포함 금액이고, 나머지는 부가세 별도(공급가액의 10%, 원 미만 절사)입니다.';
const EXTRAS_NOTE_TITLE =
  '판매가 입력도 열지 않습니다. 구독료(상위 조직 월정액)는 병원별 청구 대상이 아니고, 발신 기본료는 요금표 v70 에서 발신 건당 요금이 대신합니다.';
/** 월 사용료에 늘 들어 있는 것(요금표 v70) — 포함 통화는 위 행이 따로 말한다 */
const BASE_INCLUDES = ['070 1회선', '상담사 연결', '저장 공간 10GB'];

const uid = getCurrentInstance()?.uid ?? 0;
const titleId = `st-rate-card-${uid}`;

const isEffective = computed(() => props.mode !== 'plan');
/** 요금표 v70 이전 모양 — effective 는 `statement.legacy`(부모), plan 은 버전의 `legacy`. 표로 읽지 않는다 */
const legacy = computed(() => !!props.rates && (isEffective.value ? props.legacy : isLegacyPlan(props.rates)));
const planFrom = computed(() => (typeof props.rates?.effective_from === 'string' ? props.rates.effective_from : ''));
const cardTitle = computed(() => {
  if (isEffective.value) return '이 달 요율';
  return planFrom.value ? `${planFrom.value} 버전` : '기본 요율';
});
const ariaLabel = computed(() => (planFrom.value ? `${planFrom.value} 버전 요율` : '요율'));

// ---------------------------------------------------------------------------
// 작은 도우미(settlementRules 에 없는 것만)
// ---------------------------------------------------------------------------

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const round2 = (n) => Math.round(n * 100) / 100;

/** '월 99,000원(VAT 포함)' → ['월 99,000원', 'VAT 포함'] — 괄호 꼬리는 회색으로 뗀다 */
function splitSuffix(text) {
  const m = /^(.*?)\((VAT 포함|VAT 별도)\)$/.exec(text);
  return m ? [m[1], m[2]] : [text, ''];
}

/**
 * 칸의 원가 — effective 는 rates 에 실린 원가 스냅샷 먼저, plan 은 cost_rates 먼저(머리말 "데이터").
 * 알 길이 없으면 null(원가 칸 '—'. "확인 중"과 다르다).
 */
function costInfoOf(key) {
  return cellCostInfo(key, {
    costRates: props.costRates,
    source: props.rates,
    prefer: isEffective.value ? 'snapshot' : 'live',
  });
}

const perMessage = computed(() => {
  const v = props.costRates?.web_text_per_message ?? getPath(props.rates, ['web_text', 'cost_per_message']);
  return isNumber(v) ? v : 20;
});

const minutes = computed(() => readCell(props.rates, 'base.minutes').value);
/** '120분과 따로' 처럼 문장 안에 넣는 포함 통화 */
const minutesWord = computed(() => (isNumber(minutes.value) ? `${formatNumber(minutes.value)}분` : '포함 통화'));

// ---------------------------------------------------------------------------
// 행 보기 모델
// ---------------------------------------------------------------------------

const missingKeys = computed(() => new Set(isEffective.value ? missingPriceCellKeys(props.warnings) : []));

/** 출처 배지 — effective 에서만 */
function sourceOf(read) {
  return isEffective.value && (read.source === 'default' || read.source === 'override') ? read.source : null;
}

/** 예외 행 아래 그 달 기본값 — `default_value`(개정 3), 예전 응답이면 그 달 기본 버전에서 */
function defaultValueOf(key, read) {
  if (read.defaultValue !== undefined) return read.defaultValue;
  return props.defaultRates ? readCell(props.defaultRates, key).value : undefined;
}

function blankRow(key, label) {
  return {
    key,
    label,
    sub: '',
    source: null,
    sourceLabel: '',
    usedText: '',
    warn: false,
    warnText: '',
    unset: false,
    priceMain: '',
    priceSuffix: '',
    defaultText: '',
    costUnknown: false,
    costText: EMPTY,
    costNote: '',
    marginText: EMPTY,
    below: false,
    how: '',
    howTitle: '',
    span: false,
    spanText: '',
  };
}

/**
 * 요율 칸 하나의 행
 * @param {string} key RATE_CELLS 의 key
 * @param {{ label?: string, sub?: string, how?: string, howTitle?: string }} [opts]
 */
function cellRow(key, opts = {}) {
  const cell = RATE_CELL_MAP[key];
  const read = readCell(props.rates, key);
  const costInfo = costInfoOf(key);
  const unitSec = read.unitSec ?? costInfo?.unitSec ?? null;
  const value = read.value;
  const fmt = { perMessage: perMessage.value };
  const row = blankRow(key, opts.label ?? cell.label);
  row.sub = opts.sub ?? '';
  row.how = opts.how ?? '';
  row.howTitle = opts.howTitle ?? '';

  // 판매가 — 웹 텍스트는 '원가 그대로'(메시지당 원가는 원가 칸이 말한다)
  row.unset = cell.kind !== 'internal' && value === null;
  if (!row.unset) {
    if (cell.kind === 'pass_through') row.priceMain = value ? '원가 그대로' : '청구 안 함';
    else [row.priceMain, row.priceSuffix] = splitSuffix(formatRateValue(key, value, unitSec, fmt));
  }

  // 원가
  row.costUnknown = !!costInfo && isCostUnknown(key, costInfo);
  if (costInfo && !row.costUnknown) {
    // 웹 텍스트는 판매가 칸이 '원가 그대로'라 원가 칸이 메시지당 금액을 말한다
    const text =
      cell.kind === 'pass_through' && isNumber(costInfo.cost)
        ? `메시지당 ${formatUnitPrice(costInfo.cost)}원`
        : formatCostValue(key, costInfo);
    row.costText = text || EMPTY;
  }

  // 마진 — 같은 단위로 견줄 수 있을 때만. 원가가 버전 뒤에 다른 단위로 바뀌었으면 견주지 않는다
  const cost = costInfo?.cost;
  const unitsDiffer = cell.kind === 'price' && isNumber(read.unitSec) && isNumber(costInfo?.unitSec) && read.unitSec !== costInfo.unitSec;
  if (cell.kind === 'pass_through') {
    row.marginText = value === true ? '0원' : EMPTY;
  } else if (['price', 'per_minute', 'per_call', 'monthly'].includes(cell.kind) && isNumber(value) && isNumber(cost) && !row.costUnknown && !unitsDiffer) {
    const diff = round2(value - cost);
    row.below = diff < 0;
    row.marginText = diff < 0 ? `−${formatUnitPrice(-diff)}원` : `${formatUnitPrice(diff)}원`;
  }

  // 출처·기본값·이 달 사용량·경고
  row.source = sourceOf(read);
  row.sourceLabel = row.source ? rateSourceLabel(row.source) : '';
  if (row.source === 'override') {
    const d = defaultValueOf(key, read);
    if (d !== undefined) {
      const text = cell.kind === 'pass_through' ? (d === true ? '원가 그대로' : d === false ? '청구 안 함' : '미정') : formatRateValue(key, d, unitSec, fmt);
      row.defaultText = `기본 ${text}`;
    }
  }
  row.usedText = isEffective.value ? props.usageNotes?.[key] ?? '' : '';
  row.warn = missingKeys.value.has(key) || (!!row.usedText && row.unset);
  row.warnText = row.warn && row.unset ? '이 달 금액에서 빠집니다' : '';
  return row;
}

function lineHow(lineKey) {
  const container = getPath(props.rates, ['line', lineKey]);
  // prorate 를 빼고 저장하면 직전 버전 값을 잇는다(회신 표 14) — 칸에는 늘 있다
  const prorate = isObj(container) && container.prorate !== true ? '일할 없음' : '개통·해지 달 일할';
  return lineKey === 'uan' ? prorate : `포함 1회선 밖 · ${prorate}`;
}

/** 월 사용료의 공급가액 — ⌊VAT 포함 금액 × 10 ÷ 11⌋(개정 4 base) */
const baseHow = computed(() => {
  const fee = readCell(props.rates, 'base.monthly').value;
  if (!isNumber(fee)) return '개통 달만 일할';
  const supply = Math.floor((fee * 10) / 11);
  return `공급가액 ${formatNumber(supply)}원 + 부가세 ${formatNumber(fee - supply)}원 · 개통 달만 일할`;
});

/**
 * 상담사 번호 요금이 있는 버전·달 — 블록이 있고 유효 1개당 금액이 0 보다 크다(settlementRules baseIncludesText 와 같은 판정).
 * 그런 달만 "월 사용료에 포함"에 상담사 번호 포함 수를 붙인다.
 */
const chargedCounselor = computed(() => {
  const block = counselorNumberRates(props.rates);
  return block && isNumber(block.included) && isNumber(block.monthly) && block.monthly > 0 ? block : null;
});

const includesRow = computed(() => {
  const parts = [...BASE_INCLUDES];
  if (chargedCounselor.value) parts.push(`상담사 번호 ${formatNumber(chargedCounselor.value.included)}개`);
  return { ...blankRow('base.includes', '월 사용료에 포함'), span: true, spanText: parts.join(' · ') };
});

/**
 * 상담사 번호 추가 행(번호 요청 A3) — 요율 칸 둘(기본 포함 수·추가 1개당 월 금액)을 한 행에 "1개당 월 49,000원 VAT 별도 ·
 * 4번째 번호부터"로 보인다(숫자는 요율에서). 블록이 없는 버전·달은 '요금 없음'.
 * 유효 요율이면 출처 배지(두 칸 중 하나라도 예외면 예외 — 청구 항목 rate_source 와 같다)와 예외 칸의 그 달 기본값을 단다.
 * 이 달 쓴 양은 'counselor_number.monthly' 의 보조 표기(cellUsageNotes)다.
 */
const counselorRow = computed(() => {
  const key = 'counselor_number.monthly';
  const block = counselorNumberRates(props.rates);
  const has = !!block && isNumber(block.included) && isNumber(block.monthly);
  const row = blankRow(key, '추가 번호');
  if (!has) {
    row.priceMain = '요금 없음';
    row.how = isEffective.value ? COUNSELOR_LINE_NO_RATE_TEXT : '이 버전에는 상담사 번호 요금이 없습니다';
    return row;
  }
  row.priceMain = `1개당 월 ${formatNumber(block.monthly)}원`;
  row.priceSuffix = 'VAT 별도';
  row.how = `${formatNumber(block.included + 1)}번째 번호부터 · 등록된 날만큼 일할 · 계약 상한 밖`;
  if (isEffective.value) {
    const leaves = [
      ['counselor_number.included', readCell(props.rates, 'counselor_number.included')],
      [key, readCell(props.rates, key)],
    ];
    if (leaves.some(([, c]) => c.source === 'override')) row.source = 'override';
    else if (leaves.some(([, c]) => c.source === 'default')) row.source = 'default';
    row.sourceLabel = row.source ? rateSourceLabel(row.source) : '';
    const defaults = leaves
      .filter(([, c]) => c.source === 'override')
      .map(([k, c]) => {
        const d = defaultValueOf(k, c);
        return d === undefined ? '' : formatRateValue(k, d);
      })
      .filter(Boolean);
    if (defaults.length) row.defaultText = `기본 ${defaults.join(' · ')}`;
    row.usedText = props.usageNotes?.[key] ?? '';
  }
  return row;
});

/** 구분 행(유효 요율만) — 내부·테스트면 청구 0원 */
const internalRow = computed(() => {
  const read = readCell(props.rates, 'internal');
  const internal = read.value === true;
  const row = blankRow('internal', '구분');
  row.priceMain = categoryLabel(read.value);
  row.how = internal ? '청구 0원 확정 · 마진 계산 안 함' : '';
  row.source = sourceOf(read);
  row.sourceLabel = row.source ? rateSourceLabel(row.source) : '';
  if (row.source === 'override') {
    const d = defaultValueOf('internal', read);
    if (d !== undefined) row.defaultText = `기본 ${categoryLabel(d)}`;
  }
  return row;
});

const groups = computed(() => {
  const base = [];
  if (isEffective.value) base.push(internalRow.value);
  base.push(
    cellRow('base.monthly', { how: baseHow.value, howTitle: '개통 달만 포함 회선 사용 일수로 일할합니다' }),
    cellRow('base.minutes', {
      how: '수신 통화와 웹 음성 · 개통 달은 일할',
      howTitle: '통화마다 AI + 상담사 통화를 더해 10초로 올립니다',
    }),
    includesRow.value
  );
  const tenSec = tenSecondEquivalentText(readCell(props.rates, 'inbound.overage').value);
  return [
    { key: 'base', title: RATE_GROUP_LABELS.base, sub: '', rows: base },
    {
      key: 'call',
      title: RATE_GROUP_LABELS.call,
      sub: '10초 단위',
      rows: [
        cellRow('inbound.overage', {
          sub: '수신 · 웹 음성',
          how: `${minutesWord.value}을 넘은 10초 단위만 받습니다`,
          howTitle: [tenSec, '금액은 초과 시간 × 1분당 금액 · 항목에서 원 미만 한 번 버림'].filter(Boolean).join(' · '),
        }),
        cellRow('outbound.per_call', {
          how: `실제로 건 통화(무응답·음성사서함 포함) · ${minutesWord.value}과 따로`,
          howTitle: '걸기 전 취소·발신 실패는 0원입니다',
        }),
        cellRow('outbound.unit', {
          how: `환자가 받은 발신 통화만 · ${minutesWord.value}과 따로`,
          howTitle: 'AI + 상담사 통화를 통화마다 10초로 올립니다',
        }),
      ],
    },
    {
      key: 'line-web',
      title: `${RATE_GROUP_LABELS.line} · ${RATE_GROUP_LABELS.web}`,
      sub: '',
      rows: [
        cellRow('line.070', { how: lineHow('070') }),
        cellRow('line.uan', { how: lineHow('uan') }),
        cellRow('web_text', { how: 'studio 웹 테스트의 텍스트 메시지', howTitle: '웹 음성은 포함 통화 사용량에 듭니다' }),
      ],
    },
    { key: 'counselor-number', title: RATE_GROUP_LABELS.counselor_number, sub: '', rows: [counselorRow.value] },
    // Reference costs — every row always shown (no fold)
    {
      key: 'counselor-cost',
      title: '상담사 연결 원가',
      sub: '판매가 없음(월 사용료에 포함) · 마진 추정에만 씀',
      rows: counselorCostTableRows.value,
      note: counselorNote.value,
    },
    {
      key: 'extras',
      title: '부가 항목',
      sub: `레터링·문자·개인정보 보호 · ${extrasSummary.value}`,
      rows: extraTableRows.value,
      note: '사용량 집계 전이라 금액 계산·경고 대상이 아닙니다.',
      noteTitle: EXTRAS_NOTE_TITLE,
    },
  ];
});

function rowClass(row) {
  return {
    'is-used': !!row.usedText && !row.warn,
    'is-warn': row.warn,
    'is-override': row.source === 'override',
  };
}

// ---------------------------------------------------------------------------
// 머리·상담사 연결 원가·부가 항목·예전 모양
// ---------------------------------------------------------------------------

const versionText = computed(() => versionsLabel(props.rates));

const versionTooltip = computed(() => {
  if (legacy.value) return LEGACY_RATES_TEXT;
  const keys = overriddenCellKeys(props.rates);
  if (!keys.length) return '예외로 덮은 칸이 없습니다. 모든 칸이 기본 요율입니다.';
  const names = keys.map((key) =>
    key === 'internal' ? `구분(${categoryLabel(readCell(props.rates, 'internal').value)})` : RATE_CELL_MAP[key]?.label ?? key
  );
  return `예외로 덮은 칸 ${keys.length}개: ${names.join(', ')}`;
});

// effective 는 왜 없는지·무엇을 할지를 요율 탭 위 안내가 말한다 — 같은 말을 되풀이하지 않는다
const emptyText = computed(() => (isEffective.value ? '표시할 이 달 요율이 없습니다.' : '기본 요율 버전이 없습니다.'));

// effective 의 할 일([다시 계산]·[재수정])은 요율 탭 위 안내가 말한다 — 여기서는 왜 표가 없는지만
const legacyText = computed(() =>
  isEffective.value
    ? LEGACY_RATES_TEXT
    : '요금표 v70 이전 모양으로 저장된 버전이라 이 버전이 쓰이는 달은 금액을 계산하지 않습니다. [수정]에서 새 칸으로 다시 저장하면 풀립니다(자동으로 바꾸지 않습니다).'
);

const legacySummary = computed(() => (legacy.value ? legacyPlanSummary(props.rates) : []));

const counselorRows = computed(() =>
  counselorCostRows({ source: isEffective.value ? props.rates : null, costRates: props.costRates })
);

const counselorNote = computed(() => `STT 측정 기준 · ${STT_BASIS_LABEL} · PBX 원가는 세종 트렁크·STT 추정값입니다.`);

const extraRows = computed(() => extraItemRows(props.rates?.extras, props.costRates?.extras));

/** 상담사 연결 원가 — 판매가가 없어 마진도 없다(월 사용료에 포함) */
const counselorCostTableRows = computed(() =>
  counselorRows.value.map((r) => ({
    ...blankRow(`counselor-cost.${r.key}`, r.label),
    priceMain: '없음',
    priceSuffix: '월 사용료에 포함',
    costText: r.cost,
  }))
);

/** 부가 항목 — 판매가(지금은 전부 미정)와 원가. 사용량 집계 전이라 마진을 견주지 않는다 */
const extraTableRows = computed(() =>
  extraRows.value.map((r) => ({
    ...blankRow(`extras.${r.key}`, r.label),
    unset: r.price === '미정',
    priceMain: r.price === '미정' ? '' : r.price,
    costText: r.cost,
    costNote: r.note,
  }))
);

const extrasSummary = computed(() => {
  const unsetCount = extraRows.value.filter((r) => r.price === '미정').length;
  if (!unsetCount) return '판매가 있음';
  return unsetCount === extraRows.value.length ? '판매가 미정, 아직 청구 안 함' : `판매가 미정 ${unsetCount}개`;
});
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

.rate-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  overflow: hidden;
  color: $st-text;

  // 기본 요율 화면 — 버전 카드 안에 들어가 테두리를 부모가 그린다
  &.is-bare {
    border: 0;
    border-radius: 0;
    gap: 0;
  }

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    padding: 16px 16px 0;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.4;
    color: $st-text;
  }

  &__chip {
    @include st-pill;
    cursor: default;

    &.is-snapshot {
      @include st-pill-tone($st-accent, $st-accent-bg, $st-accent-border);
      cursor: help;
    }

    &.is-legacy {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
    }
  }

  &__extra {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 14px;
    margin-left: auto;
  }

  &__empty {
    margin: 0 16px 16px;
    padding: 20px 16px;
    border: 1px dashed $st-border-strong;
    border-radius: 10px;
    background: $st-bg;
    color: $st-text-secondary;
    font-size: 13px;
  }

  // 요금표 v70 이전 모양 — 표 대신 안내와 저장된 값 요약
  &__legacy {
    margin: 0 16px 16px;
    padding: 10px 14px;
    border: 1px solid $st-warning-border;
    border-radius: 10px;
    background: $st-warning-bg;
    color: $st-text-secondary;
    font-size: 13px;
    line-height: 1.6;

    p {
      margin: 0;
    }
  }

  &.is-bare &__empty,
  &.is-bare &__legacy {
    margin: 16px 20px;
  }

  &__legacy-list {
    margin: 6px 0 0;
    padding-left: 18px;
    color: $st-text;
  }

  &__scroll {
    overflow-x: auto;
  }

}

.rate-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 13.5px;

  th,
  td {
    padding: 11px 16px;
    // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
    border-bottom: 1px solid $st-border !important;
    text-align: left;
    vertical-align: top;
    font-variant-numeric: tabular-nums;
  }

  thead th {
    padding-top: 9px;
    padding-bottom: 9px;
    border-bottom-color: #e8e8e8;
    background: $st-bg;
    font-size: 12px;
    font-weight: 600;
    color: $st-text-secondary;
    white-space: nowrap;
  }

  .is-right {
    text-align: right;
  }

  &__help {
    cursor: help;

    :deep(.anticon) {
      margin-left: 2px;
      font-size: 11px;
      color: $st-muted;
    }
  }

  &__group th {
    padding-top: 8px;
    padding-bottom: 8px;
    background: #f7f8fa;
    font-size: 12.5px;
    font-weight: 700;
    color: $st-text;
  }

  &__group-sub {
    font-weight: 400;
    color: $st-muted;
  }

  &__item {
    width: 22%;
    min-width: 170px;
  }

  // 항목 이름 + 출처 배지, 그 아래 범위(수신 · 웹 음성)와 이 달 사용량
  tbody th.rate-table__item {
    font-weight: 400;
  }

  &__label {
    margin-right: 6px;
    font-weight: 600;
    color: $st-text;
  }

  &__sub {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: $st-muted;
  }

  &__price {
    white-space: nowrap;
  }

  &__price-main {
    font-weight: 600;
    color: $st-text;
  }

  &__price-suffix {
    // The gap is a margin — a leading space inside the span does not survive the template whitespace handling
    margin-left: 4px;
    font-size: 12.5px;
    color: $st-muted;
  }

  &__default {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: $st-muted;
  }

  &__warn {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: $st-warning;
    white-space: normal;
  }

  &__cost,
  &__margin {
    color: $st-text-secondary;
    white-space: nowrap;
  }

  &__margin.is-below {
    color: $st-warning;
    font-weight: 600;
  }

  &__below {
    display: block;
    margin-top: 2px;
    font-size: 11.5px;
    font-weight: 700;
    color: $st-warning;
  }

  &__how {
    font-size: 13px;
    color: $st-text-secondary;
    word-break: keep-all;
  }

  &__span {
    font-size: 13px;
    color: $st-text;
  }

  &__cost-note {
    display: block;
    margin-top: 2px;
    font-size: 11.5px;
    color: $st-muted;
    white-space: normal;
  }

  // Group footnote (상담사 연결 원가 STT 기준 · 부가 항목 집계 전)
  &__note-row td {
    padding-top: 7px;
    padding-bottom: 7px;
    font-size: 12px;
    color: $st-muted;
  }

  // 이 달 통화가 쓴 항목 · 쓰였는데 판매가가 없는 항목
  tr.is-used > * {
    background: $st-accent-bg;
  }

  tr.is-used > th {
    box-shadow: inset 3px 0 0 $st-accent;
  }

  tr.is-warn > * {
    background: $st-warning-bg;
  }

  tr.is-warn > th {
    box-shadow: inset 3px 0 0 $st-warning;
  }

  tbody:last-child tr:last-child > * {
    border-bottom: 0 !important;
  }
}

// 배지 — 출처(기본 회색 / 예외 노랑) · 이 달 사용량 · 미정 · 원가 확인 중
.rate-pill {
  @include st-pill;
  font-size: 11px;
  line-height: 17px;
  vertical-align: 1px;

  &.is-override {
    @include st-pill-tone($st-override, $st-override-bg, $st-override-border);
  }

  &.is-used {
    @include st-pill-tone($st-accent, #fff, $st-accent-border);
    margin-top: 4px;

    &.is-warn {
      @include st-pill-tone($st-warning, #fff, $st-warning-border);
    }
  }

  // 판매가 미정 — 출처 배지와 헷갈리지 않게 각진 모양
  &.is-unset {
    @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    line-height: 20px;
  }

  &.is-pending {
    @include st-pill-tone($st-warning, #fff, $st-warning-border);
    border-style: dashed;
    line-height: 16px;
  }
}
</style>
